import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectGroup,
  SelectLabel,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { CATEGORIAS, TIPOS_MODALIDAD, VEHICULOS, IDIOMAS, VARIANTE_NIAGARA } from "@/lib/constants";

const CATEGORIA_ORDER = ["fuera_ciudad", "dentro_ciudad", "privado"];

function NumberField({ label, value, onChange, testid, min = 0 }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input
        type="number"
        min={min}
        value={value}
        onChange={(e) => onChange(e.target.value === "" ? "" : parseInt(e.target.value, 10))}
        data-testid={testid}
      />
    </div>
  );
}

export function ReservaForm({ form, set, tours = [], agencias = [], marcas = [], showCodigo = true, reserva = null, onReservaUpdated }) {
  const [desactivandoSaldoManual, setDesactivandoSaldoManual] = useState(false);
  const grouped = CATEGORIA_ORDER.map((cat) => ({
    cat,
    items: tours.filter((t) => t.categoria === cat),
  })).filter((g) => g.items.length > 0);

  const volverSaldoAutomatico = async () => {
    if (!reserva?.id) return;
    setDesactivandoSaldoManual(true);
    try {
      const { data } = await api.post(`/reservas/${reserva.id}/saldo-manual/desactivar`);
      toast.success("Listo. El saldo va a recalcularse solo en la próxima sincronización de Bókun.");
      onReservaUpdated?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDesactivandoSaldoManual(false);
    }
  };

  const tourSel = tours.find((t) => t.id === form.tour_id);
  // Operaciones puede cambiar la modalidad SIEMPRE entre Regular / VIP / Privado,
  // sin importar las modalidades configuradas en el catálogo del tour.
  const modalidadesOpciones = ["regular", "vip", "privado"];

  // Al cambiar la modalidad sincronizamos el tipo (privado vs regular) y sugerimos el
  // vehículo (VIP siempre van; al salir de van por una regular pasamos a bus).
  const onModalidad = (v) => {
    const patch = { modalidad: v, tipo: v === "privado" ? "privado" : "regular" };
    if (v === "vip") patch.vehiculo = "van";
    else if (v === "regular" && form.vehiculo === "van") patch.vehiculo = "bus";
    set(patch);
  };

  const agenciaSel = agencias.find((a) => a.id === form.agencia_id);
  const facturada = agenciaSel ? agenciaSel.facturada !== false : false;

  return (
    <div className="space-y-4">
      {agencias.length > 0 && (
        <div className="space-y-1.5">
          <Label className="text-xs">Agencia de origen</Label>
          <Select value={form.agencia_id || "ninguna"} onValueChange={(v) => set({ agencia_id: v === "ninguna" ? "" : v })}>
            <SelectTrigger data-testid="reserva-agencia-select">
              <SelectValue placeholder="Sin agencia (venta directa)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ninguna">Sin agencia (venta directa)</SelectItem>
              {agencias.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.nombre}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {agenciaSel && facturada && (
            <p className="text-[11px] text-violet-600">
              Reserva facturada a la agencia · no se cobra saldo al pasajero.
            </p>
          )}
        </div>
      )}
      {/* PROMPT 86 (2026-09-01): cuenta/marca de la reserva cargada a mano. Solo se
          muestra cuando el contenedor pasa `marcas` (Cargar reserva manual); en
          edicion y en la Bandeja no se pasa y no aparece. */}
      {marcas.length > 0 && (
        <div className="space-y-1.5">
          <Label className="text-xs">Cuenta / marca</Label>
          <Select value={form.marca_slug || "ninguna"} onValueChange={(v) => set({ marca_slug: v === "ninguna" ? "" : v })}>
            <SelectTrigger data-testid="reserva-marca-select">
              <SelectValue placeholder="Sin especificar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ninguna">Sin especificar</SelectItem>
              {marcas.map((m) => (
                <SelectItem key={m.slug} value={m.slug}>{m.nombre}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-500">
            De qué cuenta es la reserva (Interviajes, Vacaciones, Intertravel…). Queda debajo del canal en la lista, igual que en las que entran solas.
          </p>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1.5 sm:col-span-2">
          <Label className="text-xs">Tour</Label>
          <Select
            value={form.tour_id || ""}
            onValueChange={(v) => {
              const nuevoTour = tours.find((t) => t.id === v);
              const patch = { tour_id: v };
              if (nuevoTour?.requiere_variante_barco && !form.variante) patch.variante = "sin_barco";
              set(patch);
            }}
          >
            <SelectTrigger data-testid="reserva-tour-select">
              <SelectValue placeholder="Seleccioná un tour del catálogo" />
            </SelectTrigger>
            <SelectContent>
              {grouped.map((g) => (
                <SelectGroup key={g.cat}>
                  <SelectLabel>{CATEGORIAS[g.cat]}</SelectLabel>
                  {g.items.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.nombre}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
          {form.producto_raw && (
            <p className="text-[11px] text-amber-600">Producto del correo: “{form.producto_raw}”</p>
          )}
        </div>

        {tourSel?.requiere_variante_barco && (
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs">Variante (Cataratas del Niágara)</Label>
            <div className="grid grid-cols-2 gap-2">
              {["sin_barco", "con_barco"].map((v) => {
                const active = (form.variante || "sin_barco") === v;
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => set({ variante: v })}
                    data-testid={v === "con_barco" ? "reserva-variante-con-barco" : "reserva-variante-sin-barco"}
                    className={`text-left rounded-md border px-3 py-2 text-xs font-semibold transition-colors ${
                      active ? "border-cyan-500 bg-cyan-50 text-cyan-700 ring-1 ring-cyan-500" : "border-slate-200 text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    {VARIANTE_NIAGARA[v].label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="space-y-1.5">
          <Label className="text-xs">Modalidad</Label>
          <Select value={form.modalidad} onValueChange={onModalidad}>
            <SelectTrigger data-testid="reserva-modalidad-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {modalidadesOpciones.map((m) => (
                <SelectItem key={m} value={m} data-testid={`reserva-modalidad-opt-${m}`}>
                  {TIPOS_MODALIDAD[m] || m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-400">
            VIP = grupo reducido compartido (van). Privado = tour exclusivo.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Vehículo</Label>
          <Select value={form.vehiculo || ""} onValueChange={(v) => set({ vehiculo: v })}>
            <SelectTrigger data-testid="reserva-vehiculo-select">
              <SelectValue placeholder="Auto" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(VEHICULOS).map(([k, v]) => (
                <SelectItem key={k} value={k}>
                  {v}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Fecha</Label>
          <Input
            type="date"
            value={form.fecha || ""}
            onChange={(e) => set({ fecha: e.target.value })}
            data-testid="reserva-fecha-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Hora</Label>
          <Input
            type="time"
            value={form.hora || ""}
            onChange={(e) => set({ hora: e.target.value })}
            data-testid="reserva-hora-input"
          />
        </div>

        <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {showCodigo && (
            <div className="space-y-1.5">
              <Label className="text-xs">Localizador de reserva</Label>
              <Input
                value={form.codigo_booking || ""}
                onChange={(e) => set({ codigo_booking: e.target.value })}
                placeholder="Auto si se deja vacío"
                data-testid="reserva-codigo-input"
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs">Localizador de pago</Label>
            <Input
              value={form.codigo_pago || ""}
              onChange={(e) => set({ codigo_pago: e.target.value })}
              placeholder="Automático desde Bókun (editable)"
              data-testid="reserva-codigo-pago-input"
            />
          </div>
        </div>
      </div>

      <div className="border-t border-slate-100 pt-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Pasajero</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Nombre</Label>
            <Input
              value={form.nombre || ""}
              onChange={(e) => set({ nombre: e.target.value })}
              data-testid="reserva-nombre-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Apellido</Label>
            <Input
              value={form.apellido || ""}
              onChange={(e) => set({ apellido: e.target.value })}
              data-testid="reserva-apellido-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Email (opcional)</Label>
            <Input
              value={form.email || ""}
              onChange={(e) => set({ email: e.target.value })}
              data-testid="reserva-email-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Teléfono <span className="text-red-500">*</span></Label>
            <Input
              value={form.telefono || ""}
              onChange={(e) => set({ telefono: e.target.value })}
              data-testid="reserva-telefono-input"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Nacionalidad (opcional)</Label>
            <Input
              value={form.nacionalidad || ""}
              onChange={(e) => set({ nacionalidad: e.target.value })}
              placeholder="Ej: Colombia"
              data-testid="reserva-nacionalidad-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Idioma del cliente</Label>
            <Select value={form.idioma || "es"} onValueChange={(v) => set({ idioma: v })}>
              <SelectTrigger data-testid="reserva-idioma-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(IDIOMAS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-100 pt-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
          Pasajeros y pago
        </p>
        <div className="grid grid-cols-3 gap-3">
          <NumberField label="Adultos" value={form.adultos} onChange={(v) => set({ adultos: v })} testid="reserva-adultos-input" />
          <NumberField label="Niños (4-17)" value={form.ninos} onChange={(v) => set({ ninos: v })} testid="reserva-ninos-input" />
          <NumberField label="Infantes" value={form.infantes} onChange={(v) => set({ infantes: v })} testid="reserva-infantes-input" />
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Monto pagado ({form.moneda || "USD"})</Label>
            <Input
              type="number"
              step="0.01"
              value={form.monto_pagado}
              onChange={(e) => set({ monto_pagado: e.target.value === "" ? "" : parseFloat(e.target.value) })}
              data-testid="reserva-monto-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Saldo pendiente ({form.moneda || "USD"})</Label>
            <Input
              type="number"
              step="0.01"
              value={form.saldo_pendiente}
              onChange={(e) => set({ saldo_pendiente: e.target.value === "" ? "" : parseFloat(e.target.value) })}
              data-testid="reserva-saldo-input"
            />
          </div>
        </div>
        {reserva?.saldo_manual && (
          <div
            className="mt-3 flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2"
            data-testid="saldo-manual-badge"
          >
            <p className="text-[11px] text-amber-700">
              Saldo ajustado a mano{reserva.saldo_manual_por ? ` por ${reserva.saldo_manual_por}` : ""}. El sync de Bókun no lo va a pisar.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={volverSaldoAutomatico}
              disabled={desactivandoSaldoManual}
              data-testid="volver-saldo-bokun-btn"
              className="shrink-0 h-7 text-[11px]"
            >
              {desactivandoSaldoManual ? <Loader2 className="h-3 w-3 animate-spin" /> : "Volver al importe de Bókun"}
            </Button>
          </div>
        )}
        {agenciaSel && facturada && (
          <div className="space-y-1.5 mt-3">
            <Label className="text-xs">Monto facturado a la agencia (referencia)</Label>
            <Input
              type="number"
              step="0.01"
              value={form.monto_facturado_agencia}
              onChange={(e) => set({ monto_facturado_agencia: e.target.value === "" ? "" : parseFloat(e.target.value) })}
              data-testid="reserva-monto-facturado-input"
            />
            <p className="text-[11px] text-slate-400">
              El cliente no debe saldo (facturada); este importe se usa para la facturación con la agencia.
            </p>
          </div>
        )}
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Notas</Label>
        <Textarea
          value={form.notas || ""}
          onChange={(e) => set({ notas: e.target.value })}
          rows={2}
          data-testid="reserva-notas-input"
        />
      </div>
    </div>
  );
}

export const RESERVA_FORM_VACIO = {
  codigo_booking: "",
  codigo_pago: "",
  tour_id: "",
  modalidad: "regular",
  vehiculo: "",
  tipo: "regular",
  variante: "",
  fecha: "",
  hora: "",
  nombre: "",
  apellido: "",
  email: "",
  telefono: "",
  nacionalidad: "",
  idioma: "es",
  adultos: 1,
  ninos: 0,
  infantes: 0,
  monto_pagado: 0,
  saldo_pendiente: 0,
  monto_facturado_agencia: 0,
  notas: "",
  producto_raw: "",
  agencia_id: "",
  marca_slug: "",
  moneda: "USD",
};

export function reservaToForm(r) {
  return {
    codigo_booking: r.codigo_booking || "",
    codigo_pago: r.codigo_pago || "",
    tour_id: r.tour_id || "",
    modalidad: r.modalidad || "regular",
    vehiculo: r.vehiculo || "",
    tipo: r.tipo || "regular",
    variante: r.variante || "",
    fecha: r.fecha || "",
    hora: r.hora || "",
    nombre: r.pasajero?.nombre || "",
    apellido: r.pasajero?.apellido || "",
    email: r.pasajero?.email || "",
    telefono: r.pasajero?.telefono || "",
    nacionalidad: r.pasajero?.nacionalidad || "",
    idioma: r.pasajero?.idioma || "es",
    adultos: r.adultos ?? 1,
    ninos: r.ninos ?? 0,
    infantes: r.infantes ?? 0,
    monto_pagado: r.monto_pagado ?? 0,
    saldo_pendiente: r.saldo_pendiente ?? 0,
    monto_facturado_agencia: r.monto_facturado_agencia ?? 0,
    notas: r.notas || "",
    producto_raw: r.producto_raw || "",
    agencia_id: r.agencia_id || "",
    marca_slug: r.marca || "",
    moneda: r.moneda || "USD",
  };
}

export function formToPayload(form) {
  return {
    codigo_booking: form.codigo_booking?.trim() || null,
    codigo_pago: form.codigo_pago?.trim() || null,
    tour_id: form.tour_id,
    fecha: form.fecha,
    hora: form.hora,
    modalidad: form.modalidad,
    vehiculo: form.vehiculo || null,
    tipo: form.tipo,
    variante: form.variante || null,
    adultos: parseInt(form.adultos, 10) || 0,
    ninos: parseInt(form.ninos, 10) || 0,
    infantes: parseInt(form.infantes, 10) || 0,
    monto_pagado: parseFloat(form.monto_pagado) || 0,
    saldo_pendiente: parseFloat(form.saldo_pendiente) || 0,
    monto_facturado_agencia: parseFloat(form.monto_facturado_agencia) || 0,
    moneda: form.moneda || "USD",
    notas: form.notas || "",
    agencia_id: form.agencia_id || null,
    marca_slug: form.marca_slug || null,
    pasajero: {
      nombre: form.nombre || "",
      apellido: form.apellido || "",
      email: form.email || null,
      telefono: form.telefono || null,
      nacionalidad: form.nacionalidad || null,
      idioma: form.idioma || "es",
    },
  };
}
